import { Link } from "react-router-dom";
import { Building2, Download, FilePlus, IdCard, Loader2, User } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { formatCents } from "../../../../lib/sales";
import { canMakeFile, isPaidOrder, orderBuyer, type CardOrderRow as Row } from "../../../../lib/cardOrders";
import { IconBtn, StatusPill, fmtDate } from "../../../ui";
import { OrderPick, type OrderPicks } from "../orderPicks";

/**
 * Date (with the box in front of it), order, buyer, invoice, amount, status, and the 44px
 * file button. Here rather than in the list, so the head and the rows read the same
 * tracks. The first track never goes below what the box, the icon and a date need.
 */
export const CARD_ORDER_COLS = "minmax(150px,1.05fr) minmax(0,1.3fr) minmax(0,1.5fr) .9fr .8fr .9fr 44px";

/** The order's state in one pill: paid or not, and a test payment said out loud. */
export function CardOrderStatus({ order }: { order: Pick<Row, "status" | "test"> }) {
  const t = useT();
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {isPaidOrder(order) ? (
        <StatusPill tone="ok">{t("sales.cardOrders.statusPaid")}</StatusPill>
      ) : (
        <StatusPill tone="mut">{t("sales.cardOrders.statusPending")}</StatusPill>
      )}
      {order.test ? <StatusPill tone="wt">{t("sales.cardOrders.test")}</StatusPill> : null}
    </span>
  );
}

/**
 * One order. The code opens the order; the invoice and the file keep their own clicks. The
 * last cell is the file's: the download of a kept file, or, for a paid order that keeps
 * none, the step that has one drawn.
 */
export function CardOrderRow({
  order,
  picks,
  downloading,
  making,
  onOpen,
  onDownload,
  onMake,
}: {
  order: Row;
  picks: OrderPicks;
  downloading: boolean;
  making: boolean;
  onOpen: (order: Row) => void;
  onDownload: (order: Row) => void;
  onMake: (order: Row) => void;
}) {
  const t = useT();
  const Kind = order.billing?.kind === "company" ? Building2 : User;
  const buyer = orderBuyer(order);

  return (
    // A div, not a button: the row holds a link and a button of its own. The code's button
    // stretches an ::after over the row instead, and the two controls sit above it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: CARD_ORDER_COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <OrderPick picks={picks} row={order} paid={isPaidOrder(order)} />
        <span className="crm-av crm-av-soft" style={{ width: 34, height: 34 }} aria-hidden>
          <IdCard className="size-4" />
        </span>
        <span className="crm-tnum crm-trunc text-[13px]">{fmtDate(order.createdAt)}</span>
      </span>

      <span className="min-w-0">
        <button
          type="button"
          onClick={() => onOpen(order)}
          className="crm-prow-n crm-tnum crm-trunc block max-w-full text-left after:absolute after:inset-0 after:rounded-full"
        >
          {order.code}
        </button>
        <span className="crm-prow-s crm-trunc block">
          {[order.design, t("sales.cardOrders.names", { n: order.names })].filter(Boolean).join(" · ")}
        </span>
      </span>

      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold">
          <Kind className="size-3.5 flex-none" aria-hidden />
          <span className="crm-trunc">{buyer}</span>
        </span>
        {buyer !== order.email ? <span className="crm-prow-s crm-trunc block">{order.email}</span> : null}
      </span>

      <span className="min-w-0">
        {order.invoiceId ? (
          <Link to={`/sales/invoices/${order.invoiceId}`} className="crm-tnum crm-trunc relative z-[1] block text-[13px] hover:underline">
            {order.invoiceNumber || t("sales.cardOrders.invoice")}
          </Link>
        ) : (
          <span className="crm-mut text-[12.5px]">-</span>
        )}
      </span>

      <span className="crm-tnum justify-self-end text-[13.25px] font-bold">{formatCents(order.amountCents, order.currency)}</span>

      <CardOrderStatus order={order} />

      {order.hasFile ? (
        <IconBtn
          small
          className="relative z-[1]"
          onClick={() => onDownload(order)}
          disabled={downloading}
          aria-label={t("sales.cardOrders.download")}
          title={t("sales.cardOrders.download")}
        >
          {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
        </IconBtn>
      ) : canMakeFile(order) ? (
        <IconBtn
          small
          className="relative z-[1]"
          onClick={() => onMake(order)}
          disabled={making}
          aria-label={t("sales.cardOrders.makeFile")}
          title={t("sales.cardOrders.makeFile")}
        >
          {making ? <Loader2 className="size-4 animate-spin" /> : <FilePlus className="size-4" />}
        </IconBtn>
      ) : (
        <span />
      )}
    </div>
  );
}
